import { differenceInCalendarMonths, startOfMonth } from 'date-fns';
import type { components } from '@feedfm/clips-services-api';
import { Box, Paper, TablePagination, Typography } from '@mui/material';
import { useMemo } from 'react';
import { CatalogFilterBar } from '../../../components/CatalogFilterBar';
import { CatalogSearchField } from '../../../components/CatalogSearchField';
import type { FilterFieldConfig } from '../../../components/CatalogFilterModel';
import { CatalogActiveChips } from './CatalogActiveChips';
import { findRenderedTrack } from './findSimilarModel';
import { useFindSimilar } from './useFindSimilar';
import {
  CUSTOM_CLIPS_FILTER_KEY,
  LANGUAGE_FILTER_KEY,
  POPULARITY_FILTER_KEY,
  useCatalogTable,
} from './useCatalogTable';
import { useCustomClips, type CustomClip } from '../../../components/CustomClipping';
import {
  TrackClipTable,
  type ColumnKey,
  type CustomClipSummary,
  type TrackWithClips,
} from '../../../components/TrackClipTable';
import { useStudioAuthenticatedContext } from '../../../context/StudioContext';
import { useToast } from '../../../context/ToastContext';
import { StudioPage } from '../../../page-templates/StudioPage';
import * as styles from './Catalog.styles';
import {
  columnForSort,
  nextTitleArtistSort,
  SORTABLE_COLUMN_FIELDS,
  SORTABLE_COLUMNS,
  type SortableColumnKey,
} from './catalogSort';

type TrackObject = components['schemas']['TrackObject'];
type Explicitness = components['schemas']['Explicitness'];
type Clip = components['schemas']['Clip'];

// ─── Adapter constants ──────────────────────────────────────────────────────────

/**
 * Tracks without a real curator clip get a first-N-seconds stand-in so the
 * parent row has something to show and play. Demonstration only — the editor
 * still uses the full track duration, so users can extend beyond this window.
 */
const CURATED_PREVIEW_MS = 30_000;

// ─── Filter Configuration ─────────────────────────────────────────────────────
// Mirrors the filter set the browse-only Catalog page used.

// Date Added slider spans April 2025 → the current month, in month steps.
const DATE_ADDED_ANCHOR_YEAR = 2025;
const DATE_ADDED_ANCHOR_MONTH = 3; // April, 0-based

const CATALOG_FILTERS: FilterFieldConfig[] = [
  // Row 1: Genre, Language, Popularity, Explicitness
  {
    key: 'genreContains',
    label: 'Genre',
    type: 'text',
    group: 'genre-explicit',
  },
  // Placeholders for the mocked Language / Popularity columns. Inert: stripped
  // from the catalog request (see CLIENT_ONLY_FILTER_KEYS) and not applied to
  // rows — they persist in the URL and show chips only.
  {
    key: LANGUAGE_FILTER_KEY,
    label: 'Language',
    type: 'text',
    group: 'genre-explicit',
  },
  {
    key: POPULARITY_FILTER_KEY,
    label: 'Popularity',
    type: 'threshold',
    min: 0,
    max: 100,
    step: 1,
    group: 'genre-explicit',
  },
  {
    key: 'explicitnessIs',
    label: 'Explicitness',
    type: 'select',
    group: 'genre-explicit',
    options: [
      { value: 'Explicit', label: 'Explicit' },
      { value: 'NotExplicit', label: 'Clean' },
      { value: 'ExplicitContentEdited', label: 'Censored' },
    ],
  },
  // Client-only: custom clips live in the in-memory store, not the catalog API,
  // so this selection is stripped from the server request (see useCatalogTable)
  // and applied to the visible rows here in Catalog.
  {
    key: CUSTOM_CLIPS_FILTER_KEY,
    label: 'Custom Clips',
    type: 'select',
    group: 'genre-explicit',
    options: [
      { value: 'only', label: 'Custom Clips Only' },
      { value: 'hide', label: 'Hide Custom Clips' },
    ],
  },
  // Row 3: Release Year + Date Added range sliders (shared row, side by side)
  {
    key: 'releaseYear',
    label: 'Release Year',
    type: 'range',
    minKey: 'releaseYearGreaterThan',
    maxKey: 'releaseYearLessThan',
    min: 1900,
    max: new Date().getFullYear(),
    group: 'range-sliders',
  },
  {
    key: 'dateAdded',
    label: 'Date Added',
    type: 'range',
    scale: 'month',
    minKey: 'dateAddedGreaterThan',
    maxKey: 'dateAddedLessThan',
    min: 0,
    max: differenceInCalendarMonths(
      startOfMonth(new Date()),
      new Date(DATE_ADDED_ANCHOR_YEAR, DATE_ADDED_ANCHOR_MONTH, 1),
    ),
    anchorYear: DATE_ADDED_ANCHOR_YEAR,
    anchorMonth: DATE_ADDED_ANCHOR_MONTH,
    group: 'range-sliders',
  },
];

// ─── Adapter helpers ──────────────────────────────────────────────────────────

/** Map the catalog's ExplicitStatus enum onto the clip table's Explicitness enum. */
const toExplicitness = (
  status: TrackObject['explicitness'],
): Explicitness | undefined => {
  if (status === 'Explicit') return 'explicit';
  if (status === 'ExplicitContentEdited') return 'censored';
  return undefined;
};

/**
 * Prototype-only dummy popularity in [20, 100]. Derived from the track id so it
 * looks random yet stays stable across re-renders (sorting, paging) instead of
 * flickering. Replace with a real metric when one is wired up.
 */
const dummyPopularity = (trackId: number): number =>
  20 + (Math.abs(Math.imul(trackId, 2654435761)) % 81);

/**
 * Shape a catalog TrackObject (plus any in-memory custom clips) into the
 * TrackWithClips structure TrackClipTable consumes.
 */
const adaptTrack = (
  track: TrackObject,
  customClips: CustomClipSummary[],
): TrackWithClips => {
  const trackDurationMs = track.duration_in_seconds * 1000;

  const hasCuratedClip = Boolean(track.has_curator_clip && track.curator_clip);

  // Use the real curator clip when the track has one; otherwise synthesize a
  // first-30s demonstration range with a collision-proof negative id.
  const curatedClip = hasCuratedClip
    ? {
        clipId: track.curator_clip!.id,
        startMs: track.curator_clip!.start_ms,
        endMs: track.curator_clip!.end_ms,
      }
    : {
        clipId: -track.id,
        startMs: 0,
        endMs: Math.min(CURATED_PREVIEW_MS, trackDurationMs),
      };

  return {
    trackId: track.id,
    trackTitle: track.title,
    artist: track.display_artist_name,
    album: track.release_title,
    artworkThumbnailUrl: track.artwork_thumbnail_url ?? undefined,
    artworkPreSignedUrl: track.artwork_url ?? undefined,
    audioUrl: track.track_preview?.url ?? undefined,
    explicitness: toExplicitness(track.explicitness),
    language: track.language?.name,
    popularity: dummyPopularity(track.id),
    trackDurationMs,
    dateAddedMs: new Date(track.create_date).getTime(),
    hasCuratedClip,
    curatedClip,
    customClips,
  };
};

/** Shape an in-memory custom clip into the summary TrackClipTable renders. */
const toCustomClipSummary = (clip: CustomClip): CustomClipSummary => ({
  clipId: clip.clipId,
  name: clip.name,
  startMs: clip.startMs,
  endMs: clip.endMs,
  dateAddedMs: new Date(clip.createdAt).getTime(),
});

// ─── Component ──────────────────────────────────────────────────────────────────

export const Catalog = () => {
  const {
    tracks,
    loading,
    error,
    page,
    pageSize,
    total,
    searchQuery,
    searchScope,
    filters,
    sortBy,
    sortOrder,
    setPage,
    setPageSize,
    setSearchQuery,
    setSearchScope,
    setFilters,
    setSortBy,
    setSortOrder,
    handleSort,
    handlePlayTrack,
    isTrackPlaying,
  } = useCatalogTable();

  const findSimilar = useFindSimilar();
  const similarityActive = findSimilar.seed != null;

  // Drive the server-side sort from the Title/Artist column header. Each click
  // advances the cycle (Title ↑→↓, Artist ↑→↓, clear) and resets to page one.
  const handleTitleArtistSort = () => {
    const { sortBy: nextSortBy, sortOrder: nextSortOrder } = nextTitleArtistSort(
      { sortBy, sortOrder },
    );
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
    setPage(0);
  };

  // Reflect the combined Title/Artist column's active half back to its header
  // (any non-title/artist sort leaves both labels plain).
  const titleArtistSortField =
    sortBy === 'title' || sortBy === 'artist' ? sortBy : null;

  // Standard column headers (Album, Explicitness, Date Added) use the
  // conventional asc → desc → cleared toggle that handleSort already provides.
  const handleColumnSort = (column: ColumnKey) => {
    const field = SORTABLE_COLUMN_FIELDS[column as SortableColumnKey];
    if (field) handleSort(field);
  };

  const { clipsFor, createClip, updateClipRange, renameClip, deleteClip } =
    useCustomClips();
  const { playerState } = useStudioAuthenticatedContext();
  const { showToast } = useToast();

  // Shape catalog rows into TrackClipTable's structure, merging in-memory
  // custom clips. Recomputes when the page changes or a custom clip is written
  // (clipsFor's identity changes with the store snapshot).
  const adaptedTracks = useMemo(
    () =>
      tracks.map((track) =>
        adaptTrack(track, clipsFor(track.id).map(toCustomClipSummary)),
      ),
    [tracks, clipsFor],
  );

  // Client-side Custom Clips filter. Custom clips are in-memory only, so this
  // narrows the rows already on the current page rather than the catalog query.
  const customClipsFilter = filters?.[CUSTOM_CLIPS_FILTER_KEY];

  const visibleTracks = useMemo(() => {
    if (customClipsFilter === 'only')
      return adaptedTracks.filter((t) => t.customClips.length > 0);
    if (customClipsFilter === 'hide')
      return adaptedTracks.filter((t) => t.customClips.length === 0);
    return adaptedTracks;
  }, [adaptedTracks, customClipsFilter]);

  // Similar results are real catalog TrackObjects; shape them through the same
  // adapter (merging in-memory custom clips) so they render identically.
  const similarAdapted = useMemo(
    () =>
      findSimilar.results.map((track) =>
        adaptTrack(track, clipsFor(track.id).map(toCustomClipSummary)),
      ),
    [findSimilar.results, clipsFor],
  );

  const tableTracks = similarityActive ? similarAdapted : visibleTracks;

  // Drive each row's play/pause indicator off the global player's current item.
  const isClipPlaying = (clipId: number): boolean => {
    const item = playerState.playingItem;
    // Custom clips play as a single, ranged Clip keyed by their own clipId.
    if (item && !('clips' in item)) {
      return item.clipId === clipId && playerState.playIntent;
    }
    // Parent rows play the whole track via the synthetic single-track
    // collection useCatalogTable builds; map the curated clipId back to it.
    // Search the rendered rows (tableTracks) rather than the catalog page, so
    // this resolves Find Similar results too — they aren't in adaptedTracks.
    const match = tableTracks.find((t) => t.curatedClip.clipId === clipId);
    return match ? isTrackPlaying(match.trackId) : false;
  };

  const handlePlayCuratedClip = (trackId: number) => {
    const raw = findRenderedTrack(trackId, {
      similarityActive,
      similarResults: findSimilar.results,
      catalogTracks: tracks,
    });
    if (raw) handlePlayTrack(raw);
  };

  // Custom clips have no audio of their own — they preview a [startMs, endMs]
  // window of the parent track's full-track audio in the global player.
  const handlePlayCustomClip = (clipId: number) => {
    const parent = tableTracks.find((t) =>
      t.customClips.some((c) => c.clipId === clipId),
    );
    const clip = parent?.customClips.find((c) => c.clipId === clipId);
    if (!parent || !clip) return;
    if (!parent.audioUrl) {
      showToast('No preview available for this track', 'warning');
      return;
    }
    const playable: Clip = {
      clipId: clip.clipId,
      trackId: parent.trackId,
      title: clip.name,
      artist: parent.artist,
      album: parent.album,
      duration: Math.max(1, Math.round((clip.endMs - clip.startMs) / 1000)),
      preSignedUrl: parent.audioUrl,
      preSignedUrlExpires: '',
      encoding: '',
      bitrate: 0,
      explicitness: 'unknown',
      artworkPreSignedUrl: parent.artworkPreSignedUrl,
      artworkThumbnailUrl: parent.artworkThumbnailUrl ?? null,
      createDate: '',
    };
    playerState.playClip(playable, {
      startMs: clip.startMs,
      endMs: clip.endMs,
    });
  };

  const handleFindSimilar = (track: TrackWithClips) => {
    const raw = findRenderedTrack(track.trackId, {
      similarityActive,
      similarResults: findSimilar.results,
      catalogTracks: tracks,
    });
    if (raw) findSimilar.findSimilar(raw);
  };

  return (
    <StudioPage>
      <Box sx={styles.pageContainer}>
        <Box sx={styles.headerSection}>
          <Typography variant="h4" sx={styles.pageTitle}>
            Catalog
          </Typography>
          <CatalogSearchField
            scope={searchScope}
            onScopeChange={setSearchScope}
            query={searchQuery}
            onQueryChange={setSearchQuery}
            sx={styles.headerSearch}
          />
        </Box>

        <Box sx={styles.searchAndFilters}>
          <CatalogActiveChips
            searchQuery={searchQuery}
            searchScope={searchScope}
            onClearSearch={() => {
              setSearchQuery('');
              setSearchScope('all');
            }}
            filters={filters}
            filterFields={CATALOG_FILTERS}
            onClearFilterKeys={(keys) => {
              const next = { ...(filters as Record<string, unknown> | undefined) };
              keys.forEach((k) => delete next[k]);
              setFilters(
                Object.keys(next).length > 0
                  ? (next as typeof filters)
                  : undefined,
              );
            }}
            similarToTitle={findSimilar.seed?.title ?? null}
            onClearSimilar={findSimilar.clear}
          />
          <CatalogFilterBar
            filters={filters}
            onFiltersChange={(newFilters) => setFilters(newFilters)}
            filterFields={CATALOG_FILTERS}
          />
        </Box>

        {error && (
          <Paper sx={styles.errorPaper}>
            <Typography color="error">{error}</Typography>
          </Paper>
        )}

        <TrackClipTable
          tracks={tableTracks}
          isLoading={loading || findSimilar.loading}
          onFindSimilar={handleFindSimilar}
          onTitleArtistSort={similarityActive ? undefined : handleTitleArtistSort}
          titleArtistSortField={similarityActive ? null : titleArtistSortField}
          sortableColumns={similarityActive ? undefined : SORTABLE_COLUMNS}
          activeSortColumn={similarityActive ? null : columnForSort(sortBy)}
          sortOrder={sortOrder}
          onSort={similarityActive ? undefined : handleColumnSort}
          emptyState={
            <Typography variant="body1" color="text.secondary" sx={{ py: 4 }}>
              {similarityActive
                ? 'No similar tracks found in the catalog.'
                : 'No tracks found.'}
            </Typography>
          }
          onCreateCustomClip={async (trackId, draft) => {
            await createClip(trackId, {
              name: draft.name,
              startMs: draft.startMs,
              endMs: draft.endMs,
            });
          }}
          onUpdateCustomClip={async (clipId, patch) => {
            await updateClipRange(clipId, {
              startMs: patch.startMs,
              endMs: patch.endMs,
            });
            await renameClip(clipId, patch.name);
          }}
          onDeleteCustomClip={async (clipId) => {
            await deleteClip(clipId);
          }}
          onPlayCuratedClip={handlePlayCuratedClip}
          onPlayCustomClip={handlePlayCustomClip}
          isClipPlaying={isClipPlaying}
          onEditorPreviewPlay={() => {
            // The trim editor is about to make sound — silence the global player so the two streams don't overlap.
            if (playerState.playIntent) playerState.togglePlayback();
          }}
          // The Clip Options modal shows a transport wired to the same global
          // player state as the bottom bar — one source of truth for playback.
          transport={{
            playIntent: playerState.playIntent,
            position: playerState.playbackPosition,
            duration: playerState.duration,
            volume: playerState.volume,
            onTogglePlay: () => playerState.togglePlayback(),
            onSeek: (seconds) => playerState.seek(seconds),
            onVolumeChange: (v) => playerState.setVolume(v),
            disabled: playerState.playingItem == null,
          }}
        />

        {!similarityActive && (
          <Box sx={styles.paginationWrapper}>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, newPage) => setPage(newPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </Box>
        )}
      </Box>
    </StudioPage>
  );
};
